import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../Layout/Layout';
import api from '../utils/apiClient';
import styled from 'styled-components';
import { FaGithub, FaLinkedin, FaTrophy, FaBookOpen, FaCode } from 'react-icons/fa';

// Styled Components
const ProfileContainer = styled.main`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    transform: translateX(-3px);
  }
  
  &::before {
    content: '←';
    font-size: ${({ theme }) => theme.fonts.sizes.lg};
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  box-shadow: ${({ theme }) => theme.shadows.strong}, 0 0 20px ${({ theme }) => theme.colors.cardGlow};
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    z-index: 1;
  }
`;

const ProfileHeader = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: ${({ theme }) => theme.colors.divider};
  }
  
  @media (min-width: 768px) {
    padding: ${({ theme }) => theme.spacing[8]};
  }
`;

const RankBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing[6]};
  right: ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme, rank }) => {
        if (rank === 1) return 'rgba(10, 255, 112, 0.1)';
        if (rank === 2) return 'rgba(9, 182, 255, 0.1)';
        if (rank === 3) return 'rgba(76, 131, 255, 0.1)';
        return 'rgba(255, 255, 255, 0.05)';
    }};
  color: ${({ theme, rank }) => {
        if (rank === 1) return theme.colors.primary;
        if (rank === 2) return theme.colors.secondary;
        if (rank === 3) return theme.colors.info;
        return theme.colors.textPrimary;
    }};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  border: 1px solid ${({ theme, rank }) => {
        if (rank === 1) return 'rgba(10, 255, 112, 0.3)';
        if (rank === 2) return 'rgba(9, 182, 255, 0.3)';
        if (rank === 3) return 'rgba(76, 131, 255, 0.3)';
        return 'rgba(255, 255, 255, 0.1)';
    }};
`;

const Trophy = styled(FaTrophy)`
  color: ${({ theme, rank }) => {
        if (rank === 1) return '#FFD700';
        if (rank === 2) return '#C0C0C0';
        if (rank === 3) return '#CD7F32';
        return theme.colors.textSecondary;
    }};
`;

const UserAvatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.background};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  background: ${({ background }) => background};
  box-shadow: ${({ theme }) => theme.shadows.strong};
  position: relative;
  border: 3px solid ${({ theme }) => theme.colors.backgroundDark};
  
  &::after {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background: ${({ glowColor }) => glowColor};
    border-radius: inherit;
    z-index: -1;
    opacity: 0.5;
    filter: blur(8px);
  }
`;

const UserName = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: 1px;
`;

const UserDepartment = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const UserSemester = styled.p`
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const UserRegNo = styled.p`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.backgroundLighter};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.backgroundLighter};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.background};
    transform: translateY(-3px);
  }
`;

const ProfileDetails = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  
  @media (min-width: 768px) {
    padding: ${({ theme }) => theme.spacing[8]};
  }
`;

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  
  &::after {
    content: '';
    flex-grow: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.divider};
    margin-left: ${({ theme }) => theme.spacing[4]};
  }
`;

const Bio = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  font-size: ${({ theme }) => theme.fonts.sizes.md};
`;

const EmptyBio = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.6;
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  font-style: italic;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, type }) => {
        if (type === 'xp') return 'rgba(10, 255, 112, 0.1)';
        if (type === 'courses') return 'rgba(9, 182, 255, 0.1)';
        if (type === 'modules') return 'rgba(76, 131, 255, 0.1)';
        return 'rgba(255, 255, 255, 0.05)';
    }};
  color: ${({ theme, type }) => {
        if (type === 'xp') return theme.colors.primary;
        if (type === 'courses') return theme.colors.secondary;
        if (type === 'modules') return theme.colors.info;
        return theme.colors.textPrimary;
    }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Tag = styled.span`
  display: inline-block;
  background: ${({ theme }) => theme.colors.backgroundLighter};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  transition: all ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.background};
  }
`;

const EmptyTags = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  font-style: italic;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid ${({ theme }) => theme.colors.backgroundLighter};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  box-shadow: 0 0 15px ${({ theme }) => theme.colors.glow};
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  letter-spacing: 1px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    background-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    animation: dots 1.5s infinite;
    display: inline-block;
    margin-left: 4px;
  }
  
  @keyframes dots {
    0%, 20% { opacity: 0; }
    50% { opacity: 1; }
    80%, 100% { opacity: 0; }
  }
`;

const UserProfile = () => {
    const { userId } = useParams();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                setLoading(true);
                const response = await api.users.userProfile(userId);
                setUserData(response);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching user profile:', error);
                setError('Failed to load user profile');
                setLoading(false);
            }
        };

        fetchUserProfile();
    }, [userId]);

    // Get initials for avatar
    const getInitials = (name) => {
        if (!name) return '';
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    // Get color for the avatar based on user_id and rank
    const getAvatarStyle = (rank) => {
        // Special gradients for top 3
        if (rank === 1) {
            return {
                background: 'linear-gradient(135deg, #0AFF70, #06C058)',
                glow: 'rgba(10, 255, 112, 0.6)'
            };
        }
        if (rank === 2) {
            return {
                background: 'linear-gradient(135deg, #09B6FF, #0788C0)',
                glow: 'rgba(9, 182, 255, 0.6)'
            };
        }
        if (rank === 3) {
            return {
                background: 'linear-gradient(135deg, #4C83FF, #3A63C0)',
                glow: 'rgba(76, 131, 255, 0.6)'
            };
        }

        // For other ranks - generate based on userId
        const hue = (parseInt(userId) * 137) % 360; // Generate consistent hue based on user_id
        return {
            background: `linear-gradient(135deg, hsl(${hue}, 80%, 50%), hsl(${hue + 30}, 70%, 40%))`,
            glow: `hsla(${hue}, 80%, 50%, 0.4)`
        };
    };

    // Get rank display
    const getRankDisplay = (rank) => {
        if (rank === 1) return "1st";
        if (rank === 2) return "2nd";
        if (rank === 3) return "3rd";
        return `${rank}th`;
    };

    if (loading) {
        return (
            <Layout>
                <ProfileContainer>
                    <BackButton to="/leaderboard">Back to Leaderboard</BackButton>
                    <LoadingContainer>
                        <LoadingSpinner />
                        <LoadingText>Loading profile data</LoadingText>
                    </LoadingContainer>
                </ProfileContainer>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <ProfileContainer>
                    <BackButton to="/leaderboard">Back to Leaderboard</BackButton>
                    <Card>
                        <ProfileHeader>
                            <h1>Error</h1>
                            <p>{error}</p>
                        </ProfileHeader>
                    </Card>
                </ProfileContainer>
            </Layout>
        );
    }

    if (!userData) {
        return (
            <Layout>
                <ProfileContainer>
                    <BackButton to="/leaderboard">Back to Leaderboard</BackButton>
                    <Card>
                        <ProfileHeader>
                            <h1>User Not Found</h1>
                            <p>The requested user profile could not be found.</p>
                        </ProfileHeader>
                    </Card>
                </ProfileContainer>
            </Layout>
        );
    }

    const avatarStyle = getAvatarStyle(userData.rank);

    return (
        <Layout>
            <ProfileContainer>
                <BackButton to="/leaderboard">Back to Leaderboard</BackButton>

                <Card>
                    <ProfileHeader>
                        <RankBadge rank={userData.rank}>
                            <Trophy rank={userData.rank} />
                            {getRankDisplay(userData.rank)} Rank
                        </RankBadge>

                        <UserAvatar
                            background={avatarStyle.background}
                            glowColor={avatarStyle.glow}
                        >
                            {getInitials(userData.name)}
                        </UserAvatar>

                        <UserName>{userData.name}</UserName>
                        <UserDepartment>{userData.department}</UserDepartment>
                        <UserSemester>Semester {userData.semester}</UserSemester>
              {/* <UserRegNo>{userData.registration_number}</UserRegNo> */}

                        <SocialLinks>
                            {userData.github_profile && (
                                <SocialLink
                                    href={userData.github_profile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="GitHub Profile"
                                >
                                    <FaGithub />
                                </SocialLink>
                            )}

                            {userData.linkedin_profile && (
                                <SocialLink
                                    href={userData.linkedin_profile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="LinkedIn Profile"
                                >
                                    <FaLinkedin />
                                </SocialLink>
                            )}
                        </SocialLinks>
                    </ProfileHeader>

                    <ProfileDetails>
                        <Section>
                            <SectionTitle>Bio</SectionTitle>
                            {userData.bio ? (
                                <Bio>{userData.bio}</Bio>
                            ) : (
                                <EmptyBio>This user hasn't added a bio yet.</EmptyBio>
                            )}
                        </Section>

                        <Section>
                            <SectionTitle>Stats</SectionTitle>
                            <StatsGrid>
                                <StatCard>
                                    <StatIcon type="xp">
                                        <FaTrophy />
                                    </StatIcon>
                                    <StatValue>{userData.reward_points}</StatValue>
                                    <StatLabel>XP Points</StatLabel>
                                </StatCard>

                                <StatCard>
                                    <StatIcon type="courses">
                                        <FaBookOpen />
                                    </StatIcon>
                                    <StatValue>{userData.completed_courses}</StatValue>
                                    <StatLabel>Courses Completed</StatLabel>
                                </StatCard>

                                <StatCard>
                                    <StatIcon type="modules">
                                        <FaCode />
                                    </StatIcon>
                                    <StatValue>{userData.completed_modules}</StatValue>
                                    <StatLabel>Modules Completed</StatLabel>
                                </StatCard>
                            </StatsGrid>
                        </Section>

                        
                    </ProfileDetails>
                </Card>
            </ProfileContainer>
        </Layout>
    );
};

export default UserProfile;